import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { IlllstrationBottomSkillReviewCard, IlllstrationTopSkillReviewCard } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, LinearGradient, Stack, StatusPill } from '@lyearn/ui';
import * as BaseCard from '@/modules/content/components/ContentCard/BaseCard';
import Progress from '@/modules/corporate/modules/home/views/Home/components/HomepageList/components/Progress';

import { SKILL_REVIEW_ACTION_CLASSES } from '../styles';
import { SkillReviewCardProps } from '../types';
import useAdapt from '../useAdapt';

const SkillReviewCardDefault: React.FC<SkillReviewCardProps> = (props) => {
  const { className, content } = props;
  const { t } = useTranslation(['Skills']);
  const adaptedContent = useAdapt(content);
  const showPills = adaptedContent.isOverDue || adaptedContent.progress;

  return (
    <BaseCard.Provider
      className={cn('group relative h-full', className)}
      content={adaptedContent}
      direction="col">
      <Box className="absolute inset-0">
        <LinearGradient
          className="absolute inset-0 rounded-16"
          css={'linear-gradient(141.17deg, #000000 0%, #262626 93.83%)'}
        />
        <IlllstrationTopSkillReviewCard
          className="opacity-30 absolute"
          fillColorToken="icon-white"
        />
        <IlllstrationBottomSkillReviewCard
          className="opacity-20 absolute bottom-0"
          fillColorToken="icon-white"
        />
      </Box>
      <Stack
        alignItems="start"
        className="relative flex-1 overflow-hidden rounded-16 p-20 pt-16"
        direction="col">
        {showPills ? (
          <Stack className="mb-12" direction="row" gap={8}>
            {adaptedContent.isOverDue ? (
              <StatusPill
                appearance="danger"
                label={t('Common:ContentCards.Overdue')}
                variant="light"
              />
            ) : null}
            {adaptedContent.progress ? (
              <Progress progress={adaptedContent.progress} type="small" />
            ) : null}
          </Stack>
        ) : null}
        <BaseCard.Name
          className="capitalize"
          color="text-text-white"
          numberOfLines={2}
          variant="display-l"
        />
        <BaseCard.Description
          className="uppercase opacity-60"
          color="text-text-white"
          numberOfLines={2}
          variant="display-l"
        />
        <BaseCard.PrimaryAction classes={SKILL_REVIEW_ACTION_CLASSES}>
          {adaptedContent.progress && adaptedContent.progress > 0
            ? t('Skills:Review.ContinueReviewing')
            : t('Skills:Review.StartReviewing')}
        </BaseCard.PrimaryAction>
      </Stack>
    </BaseCard.Provider>
  );
};

export default SkillReviewCardDefault;
